import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { Button } from '../../components/ui/button';
import { Download } from 'lucide-react';
import { personnelApi } from '../../services/api';
import { roleTr, departmentTr } from '../../lib/roles';

// ISO/IEC 7810 ID-1 kimlik kartı: 85.6mm × 54mm (yatay). Ölçüler mm ile verilir;
// böylece ekranda ve baskıda gerçek boyuttadır. PNG ~300 DPI'da dışa aktarılır.
const IdCardView = ({ personnel }) => {
  const cardRef = useRef(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const verifyUrl = `${window.location.origin}/personnel/verify/${personnel?.id}`;
    QRCode.toDataURL(verifyUrl, { width: 240, margin: 0 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [personnel?.id]);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // 96dpi taban → scale 3.125 ≈ 300 DPI (1011×638 px, gerçek kart boyutu)
      const canvas = await html2canvas(cardRef.current, { scale: 3.125, backgroundColor: '#ffffff', useCORS: true });
      const link = document.createElement('a');
      link.download = `kimlik-${personnel?.first_name}-${personnel?.last_name}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setDownloading(false);
    }
  };

  const fullName = `${personnel?.first_name || ''} ${personnel?.last_name || ''}`.trim();

  return (
    <div className="flex flex-col items-center gap-4">
      {/* ID-1: 85.6mm × 54mm */}
      <div
        ref={cardRef}
        style={{
          width: '85.6mm',
          height: '54mm',
          borderRadius: '3.18mm',
          boxSizing: 'border-box',
          overflow: 'hidden',
          background: '#ffffff',
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'Arial, Helvetica, sans-serif',
          color: '#0f172a',
        }}
      >
        {/* Üst bant */}
        <div
          style={{
            height: '12mm',
            background: 'linear-gradient(90deg,#1d4ed8,#2563eb)',
            color: '#fff',
            padding: '0 4mm',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <div style={{ fontSize: '3.6mm', fontWeight: 800, lineHeight: 1.05, letterSpacing: '0.2mm' }}>EMT AMBULANS</div>
          <div style={{ fontSize: '2.1mm', opacity: 0.9, marginTop: '0.4mm' }}>Personel Kimlik Kartı</div>
        </div>

        {/* Gövde */}
        <div style={{ flex: 1, display: 'flex', padding: '3mm', gap: '3mm', minHeight: 0 }}>
          {/* Foto (yaklaşık 3:4) */}
          <div
            style={{
              width: '22mm',
              height: '28mm',
              flexShrink: 0,
              borderRadius: '1.5mm',
              overflow: 'hidden',
              background: '#f1f5f9',
              border: '0.2mm solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {personnel?.photo_path ? (
              <img
                src={personnelApi.photoUrl(personnel.id)}
                alt={fullName}
                crossOrigin="anonymous"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: '10mm', fontWeight: 700, color: '#cbd5e1' }}>
                {(personnel?.first_name || '?').charAt(0)}
              </span>
            )}
          </div>

          {/* Sağ sütun */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div>
              <div style={{ fontSize: '1.8mm', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.15mm' }}>AD SOYAD</div>
              <div style={{ fontSize: '3.2mm', fontWeight: 700, lineHeight: 1.1 }}>{fullName}</div>
            </div>
            <div style={{ marginTop: '1.8mm' }}>
              <div style={{ fontSize: '1.8mm', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.15mm' }}>ÜNVAN</div>
              <div style={{ fontSize: '2.8mm', fontWeight: 700, color: '#1d4ed8', lineHeight: 1.1 }}>
                {personnel?.unvan || roleTr(personnel?.user_role) || '—'}
              </div>
            </div>

            {/* Alt: sicil + departman + QR */}
            <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '2mm' }}>
              <div style={{ display: 'flex', gap: '4mm' }}>
                <div>
                  <div style={{ fontSize: '1.7mm', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1mm' }}>SİCİL NO</div>
                  <div style={{ fontSize: '2.5mm', fontWeight: 700 }}>{personnel?.sicil_no || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '1.7mm', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1mm' }}>DEPARTMAN</div>
                  <div style={{ fontSize: '2.5mm', fontWeight: 700 }}>{departmentTr(personnel?.department) || '—'}</div>
                </div>
              </div>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR" style={{ width: '16mm', height: '16mm', flexShrink: 0 }} />
              ) : (
                <div style={{ width: '16mm', height: '16mm', background: '#f1f5f9', borderRadius: '1mm' }} />
              )}
            </div>
          </div>
        </div>

        {/* Alt şerit */}
        <div style={{ padding: '0 4mm 2mm', fontSize: '1.7mm', color: '#94a3b8' }}>
          EMT Ambulans Sağlık Hizmetleri
        </div>
      </div>

      <p className="text-[11px] text-slate-400">Standart: ISO/IEC 7810 ID-1 · 85,6 mm × 54 mm</p>

      <Button onClick={handleDownload} disabled={downloading} className="bg-blue-600 hover:bg-blue-700">
        <Download className="h-4 w-4 mr-2" />
        {downloading ? 'Hazırlanıyor...' : 'Kartı İndir (PNG)'}
      </Button>
    </div>
  );
};

export default IdCardView;
